import { GenericAbortSignal } from 'axios';

import Workflow, { WorkflowOptions } from 'models/Workflow';
import http from 'services/http';

// A map of declared value/output names -> value. The KEYS are snake_case domain
// identifiers (`first_name`, `clinical_determination`, `final_outcome`) and must be
// sent verbatim — they are not API field names, so OliveBranch leaves them alone.
export type WorkflowValuesInput = Record<string, string>;

export const workflowQueryKeys = {
  base: ['workflows'] as const,
  list: ['workflows', 'list'] as const,
  show: (id: string) => ['workflows', 'show', id] as const,
};

// The inbox: workflows the user can see (allowed_groups scope), recent first.
// A lighter per-row shape than show, but the Workflow model tolerates the missing
// definition and the lighter actions/values.
export async function indexWorkflows(signal?: GenericAbortSignal) {
  return http
    .get<{ data: WorkflowOptions[] }>(`workflows`, { signal })
    .then((res) => res.data.data.map((w) => new Workflow(w)));
}

export type CreateWorkflowInput = {
  // Top-level field keys are camelCase; OliveBranch snake_cases them on the API.
  workflowDefinitionApiName: string;
  // Exactly one group in Phase 0; must be in the user's allowed_groups.
  groupIds: string[];
  // Optional seed of demographic pills, keyed by declared value name (snake_case).
  values?: WorkflowValuesInput;
};

export async function createWorkflow(params: CreateWorkflowInput) {
  return http.post<WorkflowOptions>(`workflows`, params).then((res) => new Workflow(res.data));
}

export async function showWorkflow(id: string, signal?: GenericAbortSignal) {
  return http.get<WorkflowOptions>(`workflows/${id}`, { signal }).then((res) => new Workflow(res.data));
}

export type WriteWorkflowValuesInput = {
  workflowId: string;
  values: WorkflowValuesInput;
};

export async function writeWorkflowValues({ workflowId, values }: WriteWorkflowValuesInput) {
  return http.post<WorkflowOptions>(`workflows/${workflowId}/values`, { values }).then((res) => new Workflow(res.data));
}

export type CompleteWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  // Keyed by declared output name (snake_case), e.g. { clinical_determination: 'meets_criteria' }.
  outputs: WorkflowValuesInput;
};

export async function completeWorkflowAction({ workflowId, actionId, outputs }: CompleteWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { outputs })
    .then((res) => new Workflow(res.data));
}

// Re-run / dismiss reuse the action-update endpoint via an intent param, rather
// than the `outputs` body that completes the action.
export type WorkflowActionIntent = 'rerun' | 'dismiss';

export type InvokeWorkflowActionInput = {
  workflowId: string;
  actionId: string;
  invokeAction: WorkflowActionIntent;
};

export async function invokeWorkflowAction({ workflowId, actionId, invokeAction }: InvokeWorkflowActionInput) {
  return http
    .patch<WorkflowOptions>(`workflows/${workflowId}/actions/${actionId}`, { invokeAction })
    .then((res) => new Workflow(res.data));
}
